import { useCallback, useEffect, useMemo, useState } from 'react'

import { usePrevious } from '@dnd-kit/utilities'
import { Form } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import FormControl from 'govwell-ui/components/FormControl/FormControl'
import omit from 'lodash.omit'
import { v4 } from 'uuid'

import { PathPrefixes } from '../../constants/file'
import { FileUploadStatuses } from '../../hooks/use-file-upload-state'
import {
  UploadMultipleFilesFileUploadState,
  UseFilesUploadStateParams,
  useFilesUploadState,
} from '../../hooks/use-files-upload-state'
import { FilesUpload } from '../FilesUpload/FilesUpload'

import { makeFieldNameWithPrefix } from './util'
import Wrapper, { FormFieldWrapperProps } from './Wrapper'

interface Props extends FormFieldWrapperProps {
  fieldName: string
  uploadParams: Omit<UseFilesUploadStateParams, 'pathPrefix'> & {
    pathPrefix: PathPrefixes
  }
}

export const FilesInput = (props: Props) => {
  const { uploadParams, ...formFieldWrapperProps } = props
  const [id] = useState(v4())
  const filesUploadProps = useFilesUploadState({
    fieldName: formFieldWrapperProps.fieldName,
    ...uploadParams,
  })

  const form = Form.useFormInstance()
  const fullyQualifiedFieldName: NamePath = useMemo(
    () => makeFieldNameWithPrefix(props.fieldName, formFieldWrapperProps.fieldNamePrefix),
    [props.fieldName, formFieldWrapperProps.fieldNamePrefix]
  )
  const value = Form.useWatch(fullyQualifiedFieldName, form)
  const states: UploadMultipleFilesFileUploadState[] = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  )

  const hasLoadingFiles = states.some((s) => s.status === FileUploadStatuses.Uploading)
  const validate = useCallback(async () => {
    if (hasLoadingFiles) {
      return Promise.reject(new Error('One or more files is still loading'))
    }
    return Promise.resolve()
  }, [hasLoadingFiles])

  const previousHasLoadingFiles = usePrevious(hasLoadingFiles) || false
  const fileCount = states.length
  const previousFileCount = usePrevious(fileCount) || 0

  // Validate fields when the form state has materially changed.
  // You cannot rely on the file count alone, because the file count increases before a file is successfully uploaded, must wait for the loading state to end.
  useEffect(() => {
    if (
      (!hasLoadingFiles && !!previousHasLoadingFiles) || // A file was successfully uploaded
      (fileCount === 0 && previousFileCount !== 0) // All files were deleted
    ) {
      void form.validateFields([fullyQualifiedFieldName])
    }
  }, [
    fileCount,
    form,
    hasLoadingFiles,
    previousFileCount,
    previousHasLoadingFiles,
    fullyQualifiedFieldName,
    states.length,
  ])

  const rules = useMemo(
    () => [
      {
        validator: validate,
        validateTrigger: ['onSubmit'],
      },
      ...(props.required ? [{ required: props.required, message: props.errorMessage }] : []),
    ],
    [props.errorMessage, props.required, validate]
  )

  return (
    <Wrapper {...omit(formFieldWrapperProps, 'caption', 'label')} rules={rules} isGovWellUI>
      {({ errorMessage }) => {
        return (
          <FormControl
            tooltip={formFieldWrapperProps.tooltip}
            id={id}
            isInvalid={!!errorMessage}
            isRequired={!!formFieldWrapperProps.required}
            hideRequiredAsterisk={formFieldWrapperProps.hideRequiredAsterisk}
            caption={formFieldWrapperProps.caption}
            errorMessage={errorMessage}
            width={formFieldWrapperProps.width}
            {...(formFieldWrapperProps.label
              ? { label: formFieldWrapperProps.label }
              : { ariaLabel: formFieldWrapperProps.ariaLabel || 'Files' })}
          >
            {() => (
              <FilesUpload
                {...filesUploadProps}
                accept={uploadParams.accept}
                error={!!errorMessage}
              />
            )}
          </FormControl>
        )
      }}
    </Wrapper>
  )
}
